import { DeleteOutline as DeleteOutlineIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import type { SlaSettingsDto } from "model/slaModel";
import {
  SLA_EDITOR_TABLE_TEXT_KEYS,
  SLA_EDITOR_TEXT_KEYS,
  SLA_EDITOR_VALIDATION_KEYS,
  isSlaProcessTarget,
  validateSlaTargetDraft,
  type SlaEditorActorOption,
  type SlaOwnerOption,
  type SlaTargetDraft,
} from "model/slaEditorModel";
import type { ReactNode } from "react";
import SlaDurationFields from "./SlaDurationFields";
import SlaEscalationFields from "./SlaEscalationFields";
import SlaResponsibleFields from "./SlaResponsibleFields";
import useSlaEditorText from "./useSlaEditorText";

/**
 * Una card per target dello SLA (piano Client sez. 5 F2a): durata relativa,
 * soglia percentuale residua, utente responsabile ed escalation opzionale.
 * Le regole e i messaggi vivono in `model/slaEditorModel.ts`; qui c'e' solo la
 * resa, senza dedurre nulla dal contratto.
 *
 * Resa (2026-10): griglia a sezioni (Plazo, Riesgo, Responsable, Escalado), due
 * colonne da `md` in su e una sotto. Gli id DOM dei campi non cambiano.
 *
 * SLA per attivita' (2026-10-07): la rimozione e' un'icona cestino nell'header
 * (la conferma la chiede chi possiede la bozza).
 *
 * Designer (2026-10-08): `dense` forza una colonna, perche' il pannello
 * proprieta' del modeler e' stretto anche su schermi larghi.
 */
interface SlaTargetDraftCardProps {
  draft: SlaTargetDraft;
  /** Nome leggibile del nodo/processo; ricade sull'identificativo se assente. */
  name: string;
  /** Utenti ammessi come responsabile (amministratori, capi reparto, partecipanti). */
  ownerOptions: SlaOwnerOption[];
  ownerOptionsLoading?: boolean;
  /** Tutti gli utenti dell'organizzazione: nome del responsabile non piu' ammesso. */
  users: SlaEditorActorOption[];
  /** Ruoli per l'escalation. */
  roles: SlaEditorActorOption[];
  settings?: Pick<SlaSettingsDto, "enabled" | "escalationenabled"> | null;
  onChange: (patch: Partial<SlaTargetDraft>) => void;
  /** Rimozione del target; assente = card non rimovibile. */
  onRemove?: () => void;
  disabled?: boolean;
  /** Una sola colonna a ogni larghezza (pannello proprieta' del designer). */
  dense?: boolean;
  idPrefix: string;
}

const SlaTargetDraftCard = ({
  draft,
  name,
  ownerOptions,
  ownerOptionsLoading = false,
  users,
  roles,
  settings = null,
  onChange,
  onRemove,
  disabled = false,
  dense = false,
  idPrefix,
}: SlaTargetDraftCardProps) => {
  const translations = useTranslations();
  const text = useSlaEditorText();
  const validation = validateSlaTargetDraft(draft);
  const durationErrorKey = validation.errors.find(
    (key) => key === SLA_EDITOR_VALIDATION_KEYS.durationRequired,
  );
  const unitErrorKey = validation.errors.find(
    (key) => key === SLA_EDITOR_VALIDATION_KEYS.durationUnitRequired,
  );
  const thresholdError = validation.errors.find(
    (key) => key === SLA_EDITOR_VALIDATION_KEYS.thresholdRange,
  );

  const targetLabel = getTranslation(
    translations,
    isSlaProcessTarget(draft.targetType)
      ? SLA_EDITOR_TEXT_KEYS.targetProcess
      : SLA_EDITOR_TEXT_KEYS.targetActivity,
  );

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap title={name}>
              {name}
            </Typography>
            <Chip size="small" variant="outlined" label={targetLabel} />
          </Stack>
          {onRemove && (
            <Tooltip title={getTranslation(translations, SLA_EDITOR_TEXT_KEYS.removeTarget)}>
              <span>
                <IconButton
                  size="small"
                  data-testid={`${idPrefix}-remove`}
                  aria-label={`${getTranslation(
                    translations,
                    SLA_EDITOR_TEXT_KEYS.removeTarget,
                  )}: ${name}`}
                  disabled={disabled}
                  onClick={onRemove}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
        {/* Griglia a due colonne su desktop, una su schermi stretti: Plazo,
            Riesgo, Responsable, Escalado. Stessi campi e stessi id DOM. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: dense ? "1fr" : { xs: "1fr", md: "1fr 1fr" },
            columnGap: 3,
            rowGap: 2.5,
          }}
        >
          <SlaEditorSection label={text(SLA_EDITOR_TABLE_TEXT_KEYS.sectionDeadline)}>
            <SlaDurationFields
              draft={draft}
              onChange={onChange}
              durationErrorKey={durationErrorKey}
              unitErrorKey={unitErrorKey}
              disabled={disabled}
              idPrefix={idPrefix}
            />
          </SlaEditorSection>
          <SlaEditorSection label={text(SLA_EDITOR_TABLE_TEXT_KEYS.sectionRisk)}>
            <TextField
              id={`${idPrefix}-threshold`}
              size="small"
              type="number"
              fullWidth
              disabled={disabled}
              label={getTranslation(translations, SLA_EDITOR_TEXT_KEYS.threshold)}
              inputProps={{ min: 0, max: 100, step: "any" }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              value={draft.riskRemainingPercent ?? ""}
              error={Boolean(thresholdError)}
              helperText={
                thresholdError
                  ? getTranslation(translations, thresholdError)
                  : getTranslation(translations, SLA_EDITOR_TEXT_KEYS.thresholdHelp)
              }
              onChange={(event) => {
                const parsed = event.target.value === "" ? null : Number(event.target.value);
                onChange({
                  riskRemainingPercent:
                    parsed === null || !Number.isFinite(parsed) ? null : parsed,
                });
              }}
            />
          </SlaEditorSection>
          <SlaEditorSection label={text(SLA_EDITOR_TABLE_TEXT_KEYS.sectionResponsible)}>
            <SlaResponsibleFields
              draft={draft}
              onChange={onChange}
              options={ownerOptions}
              knownUsers={users}
              loading={ownerOptionsLoading}
              disabled={disabled}
              idPrefix={idPrefix}
            />
            {(validation.warnings ?? []).map((key) => (
              <Alert key={key} severity="warning" sx={{ py: 0 }}>
                {getTranslation(translations, key)}
              </Alert>
            ))}
          </SlaEditorSection>
          <SlaEditorSection
            label={getTranslation(translations, SLA_EDITOR_TEXT_KEYS.escalationTitle)}
          >
            <SlaEscalationFields
              draft={draft}
              onChange={onChange}
              roles={roles}
              settings={settings}
              disabled={disabled}
              idPrefix={idPrefix}
              hideTitle
            />
          </SlaEditorSection>
        </Box>
      </Stack>
    </Paper>
  );
};

/** Sezione del dettaglio: etichetta discreta sopra i campi. */
const SlaEditorSection = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Stack spacing={1.25} sx={{ minWidth: 0 }}>
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ lineHeight: 1.6, fontWeight: 700, letterSpacing: 0.8 }}
    >
      {label}
    </Typography>
    {children}
  </Stack>
);

export default SlaTargetDraftCard;
